import { ImprintMethodModel, ProductSpecModel } from '../model'
import { useApp } from '../store/modules/app'
import { type LocationMethod, type PriceInfo, OrderType } from '../types'
import {
  getAllImprintMethods,
  getImprintMethodByPriceList
} from './use-imprint'
import { useWindow } from './use-window'

const { isMultiSize, assortedMaxColors } = useWindow()

/**
 * 获取imprint method的最小购买数量
 * @param methods
 * @returns 无限制返回0，有限制返回最小数量
 */
export function getImprintMinQty (methods: LocationMethod[]): number {
  const { orderType } = useApp();
  if (orderType === OrderType.SpecSample) {
    return 1;
  }
  let minQty = 0
  for (let i = 0; i < methods.length; i++) {
    if (methods[i].MinQty === 0) return 0
    if (minQty === 0) minQty = methods[i].MinQty

    minQty = Math.min(minQty, methods[i].MinQty)
  }
  return minQty
}

/**
 * 获取imprint method的最小购买数量
 * @param methods
 * @returns 无限制返回0，有限制返回最小数量
 */
export function getImprintMaxQty (methods: LocationMethod[]): number {
  return Math.max(...methods.map((f) => f.MinQty))
}

/**
 * 根据price list自动计算最小数量
 * @param priceList
 * @param selectedImprintMethodList
 * @param widthImprintQty
 * @returns
 */
export function getMinQty (
  priceList: PriceInfo[],
  selectedImprintMethodList: ImprintMethodModel[] | undefined = undefined,
  widthImprintQty = false
): number {
  const { orderType } = useApp()
  if (orderType === OrderType.SpecSample) {
    return 1
  }

  let minQty = Math.min.call(null, ...priceList.map((it) => it.MinQty))

  if (widthImprintQty) {
    // 返回imprint的minqty
    if (!selectedImprintMethodList || selectedImprintMethodList!.length === 0) {
      const imprintMinQty = getImprintMinQty(
        getImprintMethodByPriceList(priceList)
      )
      // 取最小的minQty
      minQty = Math.max(minQty, imprintMinQty)
    } else {
      const allImprintMethods = getAllImprintMethods()
      const imprintMinQty = getImprintMinQty(
        allImprintMethods.filter((f) =>
          selectedImprintMethodList.some(
            (m) => m.decorationMethod.Method === f.Method
          )
        )
      )
      // 取最大的minqty，pricelist的minqty和imprint minqty 之间取最大minqty
      minQty = Math.max(minQty, imprintMinQty)
    }
  }
  return minQty
}

export function getMaxQty (priceList: PriceInfo[]): number {
  const { orderType } = useApp()
  if (orderType === OrderType.SpecSample) {
    return 1
  }

  let maxQty = 0
  for (let i = 0; i < priceList.length; i++) {
    const it = priceList[i]
    if (it.MaxQty === 0) {
      return 0
    } else {
      maxQty = Math.max(maxQty, it.MaxQty)
    }
  }
  return maxQty
}

/**
 * 获取商品总数量
 * @param items
 * @returns
 */
export function getTotalQty (items: ProductSpecModel[]): number {
  let total = 0
  items.forEach((it) => {
    const num = it.getQty()
    if (!isNaN(num)) {
      total += it.getQty()
    }
  })
  return total
}

/**
 * 获取assort支持的最大数量
 * @param orderType
 * @param specValues
 * @returns
 */
export function getMaxAssortNum (specValues: Record<string, string[]>): number {
  const { orderType } = useApp()
  if (orderType === OrderType.SpecSample) {
    return 1
  }
  if (isMultiSize || assortedMaxColors === 1) return 1

  // 计算可能性
  let num = 1
  for (const k in specValues) {
    num *= [...new Set(specValues[k])].length
  }

  return Math.min(assortedMaxColors, 4, num)
}

/**
 *
 */
export type CheckQtyOption = {
  isFreeGiftCustomize: boolean
  assortedMaxColor: number
  productSpecList: ProductSpecModel[]
  imprintMethodList: ImprintMethodModel[]
  priceList: PriceInfo[]
}

/**
 * 检查购买数量
 * @param options
 * @returns
 */
export function checkQty (options: CheckQtyOption): string | undefined {
  const {
    assortedMaxColors,
    assortedMinQtyPerColor,
    assortedMultipleSoldQtyPerColor,
    multipleSoldQty,
    isMultiSize
  } = useWindow()

  const { orderType, minQty, maxQty } = useApp()
  const { isFreeGiftCustomize, productSpecList, priceList } = options


  let _assortedMinQtyPerColor = assortedMinQtyPerColor
  if (assortedMinQtyPerColor === 0) {
    const assortedCount = Math.min(_assortedMinQtyPerColor, assortedMaxColors)
    _assortedMinQtyPerColor = Math.max(Math.floor(minQty / assortedCount), 1)
  }
  let showQtyError = 0
  const specColorCount = productSpecList.length
  for (let i = 0; i < productSpecList.length; i++) {
    const item = productSpecList[i]
    if (isMultiSize === false) {
      const qty = item.getQty()
      if ((isNaN(qty) || qty < 1) && (showQtyError & 2) === 0) {
        showQtyError |= 2
      } else if (orderType !== OrderType.SpecSample) {
        /*
         * 商品最小数量 25,  AssortedMinQtyPerColor = 50.
         * 如果客户只选一个颜色， 可以买  25.
         * 如果客户选超过一个颜色， 则每个颜色必须买 > =50
         * 并且客户端要给出合适的提示
         */
        if (specColorCount > 1 && (showQtyError & 8) === 0) {
          if (qty < assortedMinQtyPerColor) {
            showQtyError |= 16
          }
        }
      }
    }
  }
  const totalQty = getTotalQty(productSpecList)
  if (totalQty < minQty) {
    // 检查最小数量限制
    showQtyError |= 4
  } else if (maxQty > 0 && totalQty > maxQty) {
    // 最大数量限制
    showQtyError |= 8
  }
  if (orderType !== OrderType.SpecSample && isFreeGiftCustomize === false) {
    // 判断购买倍数
    if (totalQty % multipleSoldQty !== 0) {
      showQtyError |= 32
    }

    // 每个颜色也需要判断是否为倍数
    if (isMultiSize === false && specColorCount > 1) {
      for (let i = 0; i < specColorCount; i++) {
        const item = productSpecList[i]

        const qty = item.getQty()

        if (
          (showQtyError & 64) === 0 &&
          qty % assortedMultipleSoldQtyPerColor !== 0
        ) {
          showQtyError |= 64
        }
      }
    }
  }
  if ((showQtyError & 1) === 1) {
    return 'Please select color.'
  }
  if ((showQtyError & 2) === 2) {
    return 'Please enter quantity.'
  }
  // 倍数限制
  if ((showQtyError & 32) === 32) {
    /*
     * 商品有倍数限制且成倍数量<最小购买数量
     */
    if ((showQtyError & 4) === 4 && multipleSoldQty < minQty) {
      // 检查最小数量限制
      const tmpMinQty = Math.ceil(minQty / multipleSoldQty) * multipleSoldQty
      return (
        'A minimum of ' +
        tmpMinQty +
        ' pieces (total) is required and only increments of ' +
        multipleSoldQty +
        ' pieces.'
      )
    }

    // 商品有倍数限制且成倍数量>=最小购买数量
    return 'Please use increments of ' + multipleSoldQty + ' pieces.'
  }
  if ((showQtyError & 4) === 4) {
    // 检查最小数量限制
    return 'A minimum of ' + minQty + ' pieces (total)' + ' is required.'
  }
  if ((showQtyError & 8) === 8) {
    // 最大数量限制
    return 'Maximum quantity for this item is ' + maxQty + '.'
  }

  if ((showQtyError & 64) === 64) {
    // Assorted Color，有倍数限制且成倍数量<最小购买数量
    if (
      (showQtyError & 16) === 16 &&
      assortedMultipleSoldQtyPerColor < _assortedMinQtyPerColor
    ) {
      _assortedMinQtyPerColor =
        Math.ceil(assortedMinQtyPerColor / assortedMultipleSoldQtyPerColor) *
        assortedMultipleSoldQtyPerColor
      return (
        'Minimum of ' +
        _assortedMinQtyPerColor +
        ' pieces and increments of ' +
        assortedMultipleSoldQtyPerColor +
        ' pieces per color needed for assorted colors.'
      )
    }
    return (
      'Increments of ' +
      assortedMultipleSoldQtyPerColor +
      ' pieces per color needed for assorted colors.'
    )
  }
  if ((showQtyError & 16) === 16) {
    return (
      'Minimum of ' +
      _assortedMinQtyPerColor +
      ' pieces per color needed for assorted colors.'
    )
  }
}
